import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CourseAssignmentService } from '../../services/course-assignment/course-assignment.service';
import {
  AssignPlayersRequest,
  BulkAssignmentStrategy,
  CourseAssignmentCourse,
  CourseAssignmentOperationResult,
  CourseAssignmentPlayer,
  CourseAssignmentSummary,
} from '../../models/courseAssignment';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { TournamentService } from '../../services/tournament/tournament.service';
import { DialogService } from '../../services/shared/dialog/dialog.service';

@Component({
  selector: 'app-course-assignment',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './course-assignment.component.html',
  styleUrls: ['./course-assignment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseAssignmentComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly courseAssignmentService = inject(CourseAssignmentService);
  private readonly loggingService = inject(LoggingService);
  private readonly toastr = inject(ToastrService);
  private readonly tournamentService = inject(TournamentService);
  private readonly dialogService = inject(DialogService);

  private initializationKey: string | null = null;
  private initializing = false;
  private tournamentLoadKey: string | null = null;
  private loadingTournament = false;
  private roundLoadKey: string | null = null;

  readonly searchTerm = signal('');
  readonly statusFilter = signal<'all' | 'assigned' | 'unassigned'>('all');
  readonly selectedPlayerIds = signal<number[]>([]);
  readonly validationMessages = signal<string[]>([]);
  readonly playersCollapsed = signal(false);
  readonly coursesCollapsed = signal(false);

  private readonly routeRoundId = signal<string | null>(null);
  private readonly routeTournamentId = signal<string | null>(null);

  readonly tournament = computed(() => this.tournamentService.selectedTournamentSignal());
  readonly round = computed(() => this.tournamentService.selectedRoundSignal());
  readonly summary = computed(() => this.courseAssignmentService.summary());
  readonly players = computed(() => this.courseAssignmentService.players());
  readonly courses = computed(() => this.courseAssignmentService.courses());
  readonly loading = computed(() => this.courseAssignmentService.loading());
  readonly mutating = computed(() => this.courseAssignmentService.mutating());
  readonly error = computed(() => this.courseAssignmentService.error());

  readonly totalPlayers = computed(() => this.courseAssignmentService.totalPlayers());
  readonly assignedCount = computed(() => this.courseAssignmentService.assignedCount());
  readonly unassignedCount = computed(
    () => this.courseAssignmentService.unassignedPlayers().length
  );

  readonly assignmentProgress = computed(() => {
    const total = this.totalPlayers();
    if (!total) {
      return 0;
    }
    return Math.round((this.assignedCount() / total) * 100);
  });

  readonly filteredPlayers = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const filterMode = this.statusFilter();
    const players = [...this.players()].sort((a, b) => {
      const rankA = a.rank ?? Number.MAX_SAFE_INTEGER;
      const rankB = b.rank ?? Number.MAX_SAFE_INTEGER;
      if (rankA === rankB) {
        return a.fullName.localeCompare(b.fullName);
      }
      return rankA - rankB;
    });

    return players.filter((player) => {
      const matchesTerm = !term
        || player.fullName.toLowerCase().includes(term)
        || player.firstName.toLowerCase().includes(term)
        || player.lastName.toLowerCase().includes(term);

      if (!matchesTerm) {
        return false;
      }

      switch (filterMode) {
        case 'assigned':
          return !!player.currentAssignment;
        case 'unassigned':
          return !player.currentAssignment;
        default:
          return true;
      }
    });
  });

  readonly playersByCourse = computed(() => {
    const map = new Map<number, CourseAssignmentPlayer[]>();
    for (const course of this.courses()) {
      map.set(course.roundTournamentCourseId, []);
    }

    for (const player of this.players()) {
      const assignment = player.currentAssignment;
      if (assignment) {
        const bucket = map.get(assignment.roundTournamentCourseId);
        if (bucket) {
          bucket.push(player);
        }
      }
    }

    for (const bucket of map.values()) {
      bucket.sort((a, b) => {
        const rankA = a.rank ?? Number.MAX_SAFE_INTEGER;
        const rankB = b.rank ?? Number.MAX_SAFE_INTEGER;
        if (rankA === rankB) {
          return a.fullName.localeCompare(b.fullName);
        }
        return rankA - rankB;
      });
    }

    return map;
  });

  readonly selectedCount = computed(() => this.selectedPlayerIds().length);
  readonly hasSelection = computed(() => this.selectedCount() > 0);
  readonly allFilteredSelected = computed(() => {
    const filteredIds = this.filteredPlayers().map((player) => player.businessEntityId);
    if (!filteredIds.length) {
      return false;
    }
    const selectedIds = this.selectedPlayerIds();
    return filteredIds.every((id) => selectedIds.includes(id));
  });

  readonly canProceedToPairings = computed(() => {
    const summary = this.summary();
    return summary?.canProceedToPairings ?? false;
  });

  readonly canCopyPreviousRound = computed(() => {
    const currentRound = this.round();
    if (!currentRound) {
      return false;
    }

    const roundNumber = typeof currentRound.number === 'number'
      ? currentRound.number
      : Number(currentRound.number ?? 0);

    return roundNumber > 1;
  });

  constructor() {
    effect(
      () => {
        const availablePlayerIds = new Set(
          this.players().map((player) => player.businessEntityId)
        );
        this.selectedPlayerIds.update((ids) =>
          ids.filter((id) => availablePlayerIds.has(id))
        );
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const tournament = this.tournament();
        const roundId = this.routeRoundId();
        if (!roundId) {
          return;
        }
        const tournamentId = tournament?.tournamentId;
        if (!tournamentId) {
          return;
        }

        this.ensureRoundContext(roundId, tournamentId);
        this.startInitialization(tournamentId, roundId);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const tournamentId = this.routeTournamentId();
        const tournament = this.tournament();
        if (!tournament && tournamentId) {
          this.ensureTournamentContext(tournamentId);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.route.parent?.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const roundId = params.get('roundId');
        this.routeRoundId.set(roundId);
      });

    this.route.parent?.parent?.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const tournamentId = params.get('tournamentId');
        if (tournamentId) {
          this.routeTournamentId.set(tournamentId);
        }
      });
  }

  trackPlayer(_: number, player: CourseAssignmentPlayer): number {
    return player.businessEntityId;
  }

  trackCourse(_: number, course: CourseAssignmentCourse): number {
    return course.roundTournamentCourseId;
  }

  setStatusFilter(filterMode: 'all' | 'assigned' | 'unassigned'): void {
    this.statusFilter.set(filterMode);
  }

  updateSearchTerm(value: string): void {
    this.searchTerm.set(value);
  }

  togglePlayerSelection(playerId: number): void {
    this.selectedPlayerIds.update((ids) => {
      if (ids.includes(playerId)) {
        return ids.filter((id) => id !== playerId);
      }
      return [...ids, playerId];
    });
  }

  toggleSelectAllFiltered(): void {
    if (this.allFilteredSelected()) {
      const filteredIds = new Set(
        this.filteredPlayers().map((player) => player.businessEntityId)
      );
      this.selectedPlayerIds.update((ids) =>
        ids.filter((id) => !filteredIds.has(id))
      );
      return;
    }

    const idsToAdd = this.filteredPlayers().map((player) => player.businessEntityId);
    this.selectedPlayerIds.update((ids) => Array.from(new Set([...ids, ...idsToAdd])));
  }

  selectUnassigned(): void {
    const ids = this.courseAssignmentService
      .unassignedPlayers()
      .map((player) => player.businessEntityId);
    this.selectedPlayerIds.set(ids);
  }

  clearSelection(): void {
    this.selectedPlayerIds.set([]);
  }

  playerIsSelected(playerId: number): boolean {
    return this.selectedPlayerIds().includes(playerId);
  }

  playersAssignedToCourse(courseId: number): CourseAssignmentPlayer[] {
    return this.playersByCourse().get(courseId) ?? [];
  }

  courseSummary(courseId: number): CourseAssignmentSummary['courses'][number] | undefined {
    const summary = this.summary();
    return summary?.courses.find(
      (course) => course.roundTournamentCourseId === courseId
    );
  }

  async refreshAll(): Promise<void> {
    try {
      await this.courseAssignmentService.refreshAll();
    } catch (error) {
      this.loggingService.error('CourseAssignmentComponent', 'Failed to refresh course assignment data', error);
      this.toastr.error('Unable to refresh course assignment data.', 'Course Assignment');
    }
  }

  async refreshPlayers(): Promise<void> {
    try {
      await this.courseAssignmentService.refreshPlayers();
    } catch (error) {
      this.loggingService.error('CourseAssignmentComponent', 'Failed to refresh players', error);
      this.toastr.error('Unable to refresh players.', 'Course Assignment');
    }
  }

  async assignSelectedToCourse(course: CourseAssignmentCourse): Promise<void> {
    const playerIds = this.selectedPlayerIds();
    if (!playerIds.length) {
      this.toastr.warning('Select at least one player before assigning.', 'Course Assignment');
      return;
    }

    const players = this.players().filter((player) => playerIds.includes(player.businessEntityId));
    const alreadyAssignedDifferentCourse = players.filter(
      (player) =>
        player.currentAssignment
        && player.currentAssignment.roundTournamentCourseId !== course.roundTournamentCourseId
    );

    let overwriteExisting = false;
    if (alreadyAssignedDifferentCourse.length) {
      const confirmed = await this.dialogService.confirm(
        `${alreadyAssignedDifferentCourse.length} selected player(s) are assigned to another course. Move them to ${course.courseName}?`,
        {
          title: 'Move Players?',
          confirmLabel: 'Move Players',
          cancelLabel: 'Cancel'
        }
      );
      if (!confirmed) {
        return;
      }
      overwriteExisting = true;
    }

    const request: AssignPlayersRequest = {
      roundTournamentCourseId: course.roundTournamentCourseId,
      playerIds,
      overwriteExisting,
    };

    await this.executeAssignPlayers(request, players.length, course.courseName);
  }

  async bulkAssign(strategy: BulkAssignmentStrategy): Promise<void> {
    const tournament = this.tournament();
    const roundId = this.routeRoundId();
    if (!tournament || !roundId) {
      return;
    }

    const confirmationMessage = this.getBulkConfirmationMessage(strategy);
    if (confirmationMessage) {
      const confirmed = await this.dialogService.confirm(confirmationMessage, {
        title: 'Bulk Assign Players',
        confirmLabel: 'Run Bulk Assignment',
        cancelLabel: 'Cancel'
      });

      if (!confirmed) {
        return;
      }
    }

    const clearExisting = strategy !== 'copy-previous';

    try {
      const result = await this.courseAssignmentService.bulkAssign(strategy, clearExisting);
      this.notifyOperationResult('Bulk assignment completed.', result, true);
      this.clearSelection();
    } catch (error) {
      this.loggingService.error('CourseAssignmentComponent', `Bulk assignment (${strategy}) failed`, error);
      this.toastr.error('Bulk assignment failed. Please try again.', 'Course Assignment');
    }
  }

  async removeAssignment(player: CourseAssignmentPlayer): Promise<void> {
    if (!player.currentAssignment) {
      return;
    }

    const confirmed = await this.dialogService.confirm(
      `Remove ${player.fullName} from ${player.currentAssignment.courseName}?`,
      {
        title: 'Remove Assignment',
        confirmLabel: 'Remove',
        cancelLabel: 'Cancel'
      }
    );
    if (!confirmed) {
      return;
    }

    try {
      await this.courseAssignmentService.removeAssignment(
        player.currentAssignment.roundTournamentCourseId,
        player.businessEntityId
      );
      this.toastr.success(`${player.fullName} is now unassigned.`, 'Course Assignment');
      this.clearSelection();
    } catch (error) {
      this.loggingService.error('CourseAssignmentComponent', 'Failed to remove player assignment', error);
      this.toastr.error('Unable to remove the player assignment.', 'Course Assignment');
    }
  }

  proceedToPairings(): void {
    const summary = this.summary();
    if (!summary) {
      this.toastr.warning('Assignment summary is still loading. Please try again in a moment.', 'Course Assignment');
      return;
    }

    if (!summary.canProceedToPairings) {
      const messages = summary.validationErrors?.length
        ? summary.validationErrors
        : ['Assign all players and ensure every course has at least one player before continuing.'];
      this.validationMessages.set(messages);
      this.toastr.warning('Resolve course assignment issues before continuing.', 'Course Assignment');
      return;
    }

    this.router.navigate(['../pairings'], { relativeTo: this.route });
  }

  closeValidationMessages(): void {
    this.validationMessages.set([]);
  }

  togglePlayersPanel(): void {
    this.playersCollapsed.update(collapsed => !collapsed);
  }

  toggleCoursesPanel(): void {
    this.coursesCollapsed.update(collapsed => !collapsed);
  }

  courseHeaderClass(courseId: number): string {
    const summary = this.courseSummary(courseId);
    if (!summary) {
      return '';
    }
    if (summary.isComplete) {
      return 'course-complete';
    }
    if (summary.assignedPlayerCount === 0) {
      return 'course-empty';
    }
    return '';
  }

  formatPercentage(value: number | undefined): string {
    if (value === undefined || value === null) {
      return '0%';
    }
    return `${Math.round(value)}%`;
  }

  private async executeAssignPlayers(
    request: AssignPlayersRequest,
    selectionCount: number,
    courseName: string
  ): Promise<void> {
    try {
      const result = await this.courseAssignmentService.assignPlayers(request);
      this.notifyOperationResult(
        `${selectionCount} player(s) processed for ${courseName}.`,
        result,
        false
      );
      this.clearSelection();
    } catch (error) {
      this.loggingService.error('CourseAssignmentComponent', 'Failed to assign players to course', error);
      this.toastr.error('Unable to assign players. Please try again.', 'Course Assignment');
    }
  }

  private notifyOperationResult(
    baseMessage: string,
    result: CourseAssignmentOperationResult,
    isBulk: boolean
  ): void {
    const parts = [baseMessage];
    if (result.assignedCount) {
      parts.push(`${result.assignedCount} assigned`);
    }
    if (result.skippedCount) {
      parts.push(`${result.skippedCount} skipped`);
    }
    if (result.unassignedCount !== undefined) {
      parts.push(`${result.unassignedCount} remain unassigned`);
    }

    const summary = parts.join(' · ');
    if (result.errors.length) {
      const errorDetails = result.errors
        .map((error) => `Player #${error.businessEntityId}: ${error.reason}`)
        .join('\n');
      this.toastr.warning(`${summary}\n${errorDetails}`, 'Course Assignment');
    } else {
      const title = isBulk ? 'Bulk Assignment' : 'Course Assignment';
      this.toastr.success(summary, title);
    }
  }

  private getBulkConfirmationMessage(strategy: BulkAssignmentStrategy): string | null {
    switch (strategy) {
      case 'even':
        return 'Distribute all players evenly across courses? This will replace existing assignments.';
      case 'rank-alternate':
        return 'Assign players alternating by rank across courses? Existing assignments will be replaced.';
      case 'random':
        return 'Randomly assign all players across courses? Existing assignments will be replaced.';
      case 'copy-previous':
        return 'Copy assignments from the previous round? New players will remain unassigned.';
      default:
        return null;
    }
  }

  private ensureTournamentContext(tournamentId: string): void {
    if (this.loadingTournament || this.tournamentLoadKey === tournamentId) {
      return;
    }

    this.loadingTournament = true;
    this.tournamentService
      .getById(tournamentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tournament) => {
          this.tournamentService.selectedTournament = tournament;
          this.tournamentLoadKey = tournamentId;
        },
        error: (error) => {
          this.loggingService.error('CourseAssignmentComponent', 'Failed to load tournament context', error);
          this.toastr.error('Unable to load tournament context. Please select a tournament first.', 'Course Assignment');
          this.router.navigate(['/tournaments']);
        },
        complete: () => {
          this.loadingTournament = false;
        },
      });
  }

  private ensureRoundContext(roundId: string, tournamentId: string): void {
    if (this.round()?.roundId === roundId || this.roundLoadKey === roundId) {
      return;
    }

    this.roundLoadKey = roundId;
    this.tournamentService
      .getRounds(tournamentId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rounds) => {
          const match = rounds.find((round) => round.roundId === roundId);
          if (match) {
            this.tournamentService.selectedRound = match;
          }
        },
        error: (error) => {
          this.loggingService.error('CourseAssignmentComponent', 'Failed to load round context', error);
        },
      });
  }

  private startInitialization(tournamentId: string, roundId: string): void {
    const key = `${tournamentId}:${roundId}`;
    if (this.initializing || this.initializationKey === key) {
      return;
    }

    this.initializing = true;
    this.courseAssignmentService
      .initialize(tournamentId, roundId)
      .then(() => {
        this.initializationKey = key;
        this.loggingService.info('CourseAssignmentComponent', `Course assignment initialized for tournament ${tournamentId}, round ${roundId}`);
      })
      .catch((error) => {
        this.loggingService.error('CourseAssignmentComponent', 'Failed to initialize course assignment data', error);
        this.toastr.error('Unable to load course assignment data.', 'Course Assignment');
      })
      .finally(() => {
        this.initializing = false;
      });
  }

}
